import { useEffect, useState } from 'react';
import { Progress, Typography, Space, Spin, Tooltip } from 'antd';
import { InfoCircleOutlined, TrophyOutlined } from '@ant-design/icons';
import { reportingService } from '../../services/reporting-service';
import { PowerHourTimer } from './power-hour-timer';
import { PowerHourConfig, PowerHourData } from '../../dto/reporting-models';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import { strings } from '../../lang';
import { getLocalTimeBasedOnHoursAndMins } from '../../utils/time-utils';

const { Text } = Typography;

type PowerHourRaceProps = {
  powerHourConfig: PowerHourConfig;
  refreshKey?: number;
  setIsLoading?: (loading: boolean) => void;
};

const getInitials = (teamName: string) => {
  return teamName
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const PowerHourRace = ({ powerHourConfig, refreshKey, setIsLoading }: PowerHourRaceProps) => {
  const { instance, inProgress } = useMsal();
  const [powerHourData, setPowerHourData] = useState<PowerHourData[] | null>(null);
  useEffect(() => {
    (async () => {
      setIsLoading?.(true);
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const powerhourData = await reportingService.getActivePowerHourData(powerHourConfig, token.accessToken);
      setPowerHourData(powerhourData);
      setIsLoading?.(false);
    })();
  }, [refreshKey]);

  // Sort by experiencePerContributor descending
  const sortedData = powerHourData ? [...powerHourData].sort((a, b) => (b.experiencePerContributor || 0) - (a.experiencePerContributor || 0)) : [];

  const maxXP = sortedData.length > 0 ? Math.max(...sortedData.map((ph) => ph.experiencePerContributor || 0)) : 1;

  const endTime = getLocalTimeBasedOnHoursAndMins(powerHourConfig.endTimeUtc);

  return (
    <>
      {powerHourData === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 15 }}>
            <span style={{ marginRight: 8 }}>
              {strings.powerHour.description}
              <span style={{ marginLeft: 4 }}>
                <Tooltip title={strings.powerHour.descriptionNote}>
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            </span>
            <div
              style={{
                padding: '6px 0',
                fontSize: 16,
              }}
            >
              <PowerHourTimer endTime={endTime} />
            </div>
          </div>

          <div
            style={{
              background: 'linear-gradient(135deg, #6DD5FA 0%, #2980B9 60%, #4E89D0 100%)',
              borderRadius: 8,
              padding: 16,
              paddingBottom: 0,
            }}
          >
            {sortedData.map((powerHour, index) => (
              <div key={powerHour.id} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ flex: '0 1 75%', position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={powerHour.teamName}>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--color-white)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#4E89D0',
                          marginRight: '8px',
                          zIndex: 1,
                        }}
                      >
                        {getInitials(powerHour.teamName)}
                      </div>
                    </Tooltip>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <Progress percent={Math.round((powerHour.experiencePerContributor / maxXP) * 100)} showInfo={false} strokeColor="var(--color-white)" trailColor="rgba(255, 255, 255, 0.3)" />
                      <div
                        style={{
                          position: 'absolute',
                          right: `${100 - Math.round((powerHour.experiencePerContributor / maxXP) * 100)}%`,
                          transform: 'translateX(100%)',
                          top: '50%',
                          marginTop: '-15px',
                          zIndex: 2,
                          marginRight: -10,
                        }}
                      >
                        <img
                          src="/boat.png"
                          alt="Boat"
                          style={{
                            height: '24px',
                            width: 'auto',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: '0 1 30%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Space>
                      {index === 0 && <TrophyOutlined style={{ color: 'var(--color-gold)' }} />}
                      <Text style={{ color: 'var(--btn-text-on-colored)', fontWeight: index === 0 ? 'bold' : undefined, whiteSpace: 'nowrap' }}>{powerHour.experiencePerContributor} XP/Booker</Text>
                    </Space>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default PowerHourRace;
